(() => {
  'use strict';
  const ROOT = window.DluxcaAuditConfig?.root || window.Shopify?.routes?.root || '/';
  const MOQ = Math.max(1, Math.ceil(Number(window.DluxcaAuditConfig?.preorderMOQ) || 30));
  let cartBusy = false;
  function integer(value, label = 'Quantity') {
    const n = Number(value);
    if (!Number.isSafeInteger(n) || n < 1 || n > 99999) throw new Error(`${label} must be a whole number from 1 to 99999.`);
    return n;
  }
  async function json(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(url, {credentials:'same-origin', cache:'no-store', ...options, signal: options.signal || controller.signal});
      let data;
      try { data = await response.json(); } catch (_) { throw new Error('Unexpected store response. Refresh this page.'); }
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : data.message || 'The store could not complete the request.');
      return data;
    } finally { clearTimeout(timer); }
  }
  const cartQty = (cart, id) => cart.items.filter(x => String(x.variant_id || x.id) === String(id)).reduce((n,x) => n + x.quantity, 0);
  async function variantData(handleOrUrl, id) {
    if (!/^\d+$/.test(String(id))) throw new Error('Please select an available product option.');
    if (!handleOrUrl) throw new Error('Product details are unavailable. Please reopen this product.');
    const url = new URL(String(handleOrUrl).includes('/') ? handleOrUrl : ROOT + 'products/' + encodeURIComponent(handleOrUrl), location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product link.');
    url.searchParams.set('variant', String(id)); url.searchParams.set('view','dluxca-quick-order-data'); url.searchParams.set('_dluxca',Date.now());
    const data = await json(url);
    if (data.quick_order_version !== 2 || String(data.variant_id) !== String(id)) throw new Error('Product availability could not be checked. Please refresh this page.');
    return data;
  }
  function validate(data, qty, existing = 0) {
    integer(qty);
    if (!data.available) throw new Error('This option is sold out.');
    const r = data.quantity_rule;
    if (!r || !Number.isInteger(r.min) || r.min < 1 || !Number.isInteger(r.increment) || r.increment < 1 || (r.max !== null && (!Number.isInteger(r.max) || r.max < 1))) throw new Error('Product quantity rules are unavailable.');
    const min = Math.max(r.min, data.preorder ? (data.preorder_moq || MOQ) : 1);
    if (qty < min) throw new Error(`${data.preorder ? 'Preorders' : 'This option'} require at least ${min} units. Please update the quantity.`);
    if (qty % r.increment !== 0) throw new Error(`Use quantities in multiples of ${r.increment}.`);
    if (r.max !== null && existing + qty > r.max) throw new Error(`Maximum ${r.max} units per option; ${existing} already in your cart.`);
    if (data.tracked && !data.preorder) {
      if (!Number.isInteger(data.inventory_quantity)) throw new Error('Inventory could not be checked.');
      if (existing + qty > data.inventory_quantity) throw new Error(`Only ${Math.max(0,data.inventory_quantity - existing)} more units available.`);
    }
    return qty;
  }
  async function validateForm(form, url) {
    if (!form) throw new Error('Product form not found.');
    const id = form.querySelector('[name=id]')?.value;
    const qty = integer(form.querySelector('[name=quantity]')?.value || 1);
    const [data, cart] = await Promise.all([variantData(url,id), json(ROOT+'cart.js')]);
    if (form.querySelector('[name=id]')?.value !== id || integer(form.querySelector('[name=quantity]')?.value || 1) !== qty) throw new Error('Your option or quantity changed. Please add it again.');
    validate(data,qty,cartQty(cart,id));
    let prop = form.querySelector('[name="properties[pre_order_item]"]');
    if (data.preorder) {
      if (!prop) { prop=document.createElement('input');prop.type='hidden';prop.name='properties[pre_order_item]';form.append(prop); }
      prop.value='Yes';
    } else prop?.remove();
    return data;
  }
  async function addItems(rawItems) {
    if(cartBusy) throw new Error('Please wait for the current cart update to finish.');
    cartBusy=true;
    let submitted=false;
    try {
      const groups=new Map();
      for(const item of rawItems){
        const id=String(item.variant_id || item.id || ''); const qty=integer(item.quantity);
        if(!/^\d+$/.test(id)) throw new Error('A saved option is no longer available. Remove it from your selection.');
        const group=groups.get(id);
        if(group) group.quantity=integer(group.quantity+qty);
        else groups.set(id,{id,quantity:qty,handle:item.product_handle || item.handle});
      }
      if(!groups.size) throw new Error('Select at least one item.');
      const before=await json(ROOT+'cart.js');const ready=[]; const failedItems=[];
      // Bound requests while checking all items before any write.
      const items=[...groups.values()];let cursor=0;
      await Promise.all(Array.from({length:Math.min(4,items.length)},async()=>{
        while(cursor<items.length){const g=items[cursor++];try{
          const data=await variantData(g.handle,g.id);
          if(data.requires_selling_plan || data.requires_components || data.gift_card) throw new Error('Open this product to choose its purchase options.');
          validate(data,g.quantity,cartQty(before,g.id));
          ready.push({id:g.id,quantity:g.quantity,...(data.preorder ? {properties:{pre_order_item:'Yes'}} : {})});
        }catch(e){failedItems.push({id:g.id,message:e.message});}}
      }));
      if(!ready.length) throw new Error(failedItems[0]?.message || 'No available items.');
      // One request per batch; never automatically retry a write with an uncertain result.
      for(let start=0;start<ready.length;start+=20){
        submitted=true;
        try { await json(ROOT+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:ready.slice(start,start+20)})}); }
        catch(e){throw new Error(`Cart update stopped: ${e.message} Open your cart and check quantities before trying again; some items may already have been added.`);}
      }
      const after=await json(ROOT+'cart.js');
      if(ready.some(x=>cartQty(after,x.id)-cartQty(before,x.id)!==x.quantity)) throw new Error('Cart quantities could not be confirmed. Open your cart before adding again.');
      if(failedItems.length){
        // Expose concrete validation reasons, including preorder MOQ, instead of treating everything as sold out.
        window.dispatchEvent(new CustomEvent('dluxca:cart-notice',{detail:failedItems.map(x=>x.message).slice(0,3).join(' ')}));
      }
      return {addedLines:ready.length,addedUnits:ready.reduce((n,x)=>n+x.quantity,0),failedItems};
    } finally {
      cartBusy=false;
      if(submitted) window.dispatchEvent(new Event('dluxca:cart-changed'));
    }
  }
  function message(form, text, error = false) {
    let node = form.querySelector('[data-dluxca-form-status]');
    if (!node) { node=document.createElement('p'); node.dataset.dluxcaFormStatus='';node.setAttribute('role','status');node.setAttribute('aria-live','polite');form.append(node); }
    node.textContent=text;node.hidden=!text;node.dataset.error=String(error);
  }
  function syncForm(form) {
    const source=form.querySelector('[data-dluxca-product]'); if(!source)return;
    const field=form.querySelector('[name=id]');const input=form.querySelector('[name=quantity]');
    if(!field||!input)return;
    try {
      const option=field.selectedOptions?.[0];
      const rule=JSON.parse(option?.dataset.dluxcaRule || source.textContent);
      if(String(rule.id)!==field.value)return;
      const preorderNote=form.querySelector('[data-dluxca-preorder-note]');
      if(preorderNote){preorderNote.hidden=!rule.preorder;const label=preorderNote.querySelector('[data-dluxca-preorder-label]');if(label && rule.preorder)label.textContent=`Preorder only · Minimum order: ${rule.min} units.`;}
      input.min=rule.min;input.step=rule.step;
      if(rule.max!==null)input.max=rule.max;else input.removeAttribute('max');
      if(form.dataset.auditVariant!==field.value){input.value=rule.min;form.dataset.auditVariant=field.value;}
      let prop=form.querySelector('[name="properties[pre_order_item]"]');
      if(rule.preorder){if(!prop){prop=document.createElement('input');prop.type='hidden';prop.name='properties[pre_order_item]';form.append(prop);}prop.value='Yes';}
      else prop?.remove();
    }catch(_){/* Submission validates fresh server metadata. */}
  }
  async function submitForm(form, button) {
    if(cartBusy){message(form,'Please wait for the current cart update.',true);return;}
    if(form.dataset.auditVariantPending==='true'){message(form,'Please wait for the selected option to finish loading.',true);return;}
    if(!form.reportValidity())return;
    const source=form.querySelector('[data-dluxca-product]');if(!source)return;
    cartBusy=true;
    const original=button?.textContent;let attempted=false;
    if(button){button.disabled=true;button.textContent='Adding…';}
    message(form,'');
    try {
      const data=await validateForm(form,source.dataset.productUrl);
      if(form.dataset.auditVariantPending==='true')throw new Error('Your product option changed. Please add it again.');
      if(data.requires_components)throw new Error('Please use the bundle purchase options for this product.');
      const payload=new FormData(form);
      const id=payload.get('id');const qty=integer(payload.get('quantity')||1);
      attempted=true;
      const added=await json(ROOT+'cart/add.js',{method:'POST',headers:{Accept:'application/json'},body:payload});
      message(form,'Added to cart.');
      form.dispatchEvent(new CustomEvent('product:added',{bubbles:true,detail:{variant:added,button,quantity:qty}}));
      if(window.theme?.cartType==='page')location.assign(ROOT+'cart');
    }catch(e){message(form,(e.name==='AbortError'?'The request timed out.':e.message)+(attempted?' Check your cart before trying again; the request was not automatically repeated.':''),true);}
    finally {
      cartBusy=false;
      if(attempted)window.dispatchEvent(new Event('dluxca:cart-changed'));
      if(button?.isConnected){button.disabled=false;button.textContent=original;}
    }
  }
  // Capture before Warehouse's delegated click handlers. Only marked product forms are owned here.
  window.addEventListener('click',e=>{
    const button=e.target.closest?.('[data-action="add-to-cart"],.collection-atc-btn');
    if(!button||button.disabled)return;
    const form=button.form || button.closest('form');
    if(!form?.querySelector('[data-dluxca-product]'))return;
    e.preventDefault();e.stopImmediatePropagation();void submitForm(form,button);
  },true);
  window.addEventListener('submit',e=>{
    const form=e.target;
    if(!form.matches?.('form[action*="/cart/add"]')||!form.querySelector('[data-dluxca-product]'))return;
    e.preventDefault();e.stopImmediatePropagation();void submitForm(form,e.submitter||form.querySelector('[type=submit]'));
  },true);
  document.addEventListener('change',e=>{
    const form=e.target.form;
    if(!form?.querySelector('[data-dluxca-product]'))return;
    if(e.target.matches('[data-option-position]'))form.dataset.auditVariantPending='true';
    if(e.target.name==='id')syncForm(form);
  },true);
  document.addEventListener('product:rerender',e=>{
    queueMicrotask(()=>{e.target.dataset.auditVariantPending='false';syncForm(e.target);});
  },true);
  let pendingSync=false;
  const scan=()=>document.querySelectorAll('form[action*="/cart/add"]').forEach(syncForm);
  new MutationObserver(records=>{
    if(pendingSync||!records.some(r=>r.addedNodes.length))return;
    pendingSync=true;requestAnimationFrame(()=>{pendingSync=false;scan();});
  }).observe(document.documentElement,{childList:true,subtree:true});
  scan();
  window.addEventListener('dluxca:cart-changed',()=>document.documentElement.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true,detail:{scrollToTop:false}})));
  window.addEventListener('dluxca:cart-notice',e=>{window.alert(e.detail);});

  window.addEventListener('change',function(event){
    const input=event.target.closest?.('input[data-dluxca-cart-min]');
    if(!input)return;
    const min=Number(input.dataset.dluxcaCartMin),step=Number(input.step)||1;
    const value=Number(input.value);
    if(Number.isInteger(value)&&value>=min&&value%step===0)return;
    input.value=Math.max(min,Math.ceil((Number.isFinite(value)?value:min)/step)*step);
    let note=input.parentElement.querySelector('[data-dluxca-quantity-notice]');
    if(!note){note=document.createElement('span');note.dataset.dluxcaQuantityNotice='';note.setAttribute('role','status');input.parentElement.append(note);}
    note.textContent=`Preorder minimum: ${min} units. Quantity adjusted to ${input.value}.`;
    // The theme's existing change handler writes the corrected quantity to Shopify.
  },true);
  window.DluxcaAudit={integer,json,variantData,validate,cartQty,addItems,submitForm};
})();
